// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import type { Bundle } from '../data';
import { stateColour, stateName } from '../data';
import { num, signed, pct1, safe, tipAttr, escapeHtml, signedPct, growthPct } from '../format';
import { attachSvgZoom } from '../utils/svgZoom';

/**
 * The site's only counterfactual: growth measured against EXPECTATION rather
 * than against the past.
 *
 * The solid curve is the cumulative share of national growth, with regions
 * ranked by how much they contributed. The dashed curve is where that same
 * growth would have landed had every region grown in proportion to the workforce
 * it already had. The vertical gap between them is the dispersal, and Canberra —
 * the largest single workforce in the country — sits visibly below its own
 * fair-share position.
 */

interface Step {
  name: string; state: string | null;
  growth: number; baseShare: number; fairShare: number;
}

export function renderGrowth(root: HTMLElement, b: Bundle, pinned: string | null): void {
  const years = b.map.years;
  let baseYear = b.map.baseYear;
  const latest = b.map.latestYear;

  root.innerHTML = `
    <div class="view-head">
      <h2>Did the growth land where the workforce already was?</h2>
      <p>Regions ranked by how much of the national increase they contributed. The dashed line is the counterfactual: where the same growth would have gone if every region had grown in proportion to the staff it already had.</p>
    </div>
    <div class="controls">
      <div class="control-group">
        <span class="control-label">Measure growth since</span>
        <select id="base-year"></select>
      </div>
    </div>
    <div class="panel">
      <div class="section-title">Concentration of growth</div>
      <div class="section-sub" id="growth-sub"></div>
      <div class="chart-wrap" id="growth-wrap"></div>
      <div class="legend">
        <span class="legend-item"><span class="legend-swatch" style="background:var(--accent-primary)"></span> Actual cumulative share of growth</span>
        <span class="legend-item"><span class="legend-swatch" style="background:transparent;border:1.5px dashed var(--text-tertiary)"></span> If growth followed existing workforce share</span>
      </div>
    </div>
    <div class="panel">
      <div class="section-title">Fair-share ledger</div>
      <div class="section-sub">Every region's actual growth against the growth its starting size would have predicted. A negative shortfall means the region grew less than its size implied.</div>
      <div class="scroller"><table class="data" id="ledger"></table></div>
    </div>`;

  const sel = root.querySelector<HTMLSelectElement>('#base-year')!;
  years.slice(0, -1).forEach((y) => {
    const o = document.createElement('option');
    o.value = String(y); o.textContent = String(y);
    if (y === baseYear) o.selected = true;
    sel.appendChild(o);
  });

  function build(): Step[] {
    const rows = b.map.regions.map((r) => {
      const then = r.values[String(baseYear)] ?? 0;
      const now = r.values[String(latest)] ?? 0;
      return { name: r.name, state: r.state, growth: now - then, base: then };
    });
    const totalBase = rows.reduce((s, r) => s + r.base, 0);
    const totalGrowth = rows.reduce((s, r) => s + r.growth, 0);
    return rows
      .map((r) => ({
        name: r.name, state: r.state, growth: r.growth,
        baseShare: totalBase ? r.base / totalBase : 0,
        fairShare: totalBase ? (r.base / totalBase) * totalGrowth : 0,
      }))
      .sort((a, c) => c.growth - a.growth);
  }

  function draw(): void {
    const steps = build();
    const totalGrowth = steps.reduce((s, r) => s + Math.max(0, r.growth), 0);
    const netGrowth = steps.reduce((s, r) => s + r.growth, 0);
    const W = 900, H = 420, L = 54, R = 16, T = 18, B = 46;
    const iw = W - L - R, ih = H - T - B;
    const n = steps.length;
    const xAt = (i: number) => L + (n <= 1 ? 0 : (i / n) * iw);
    const yAt = (frac: number) => T + ih - safe(frac) * ih;

    let cum = 0, cumFair = 0;
    const actual: string[] = [`M${L},${yAt(0)}`];
    const fair: string[] = [`M${L},${yAt(0)}`];
    const marks: string[] = [];
    steps.forEach((s, i) => {
      cum += Math.max(0, s.growth);
      cumFair += Math.max(0, s.fairShare);
      const x = xAt(i + 1);
      actual.push(`L${x.toFixed(1)},${yAt(cum / (totalGrowth || 1)).toFixed(1)}`);
      fair.push(`L${x.toFixed(1)},${yAt(cumFair / (totalGrowth || 1)).toFixed(1)}`);
      if (i < 14 || s.name === pinned) {
        const cy = yAt(cum / (totalGrowth || 1));
        marks.push(
          `<circle class="bar" cx="${x.toFixed(1)}" cy="${cy.toFixed(1)}" r="${s.name === pinned ? 6 : 3.4}" `
          + `fill="${stateColour(s.state)}" stroke="#fff" stroke-width="1" tabindex="0" role="button" `
          + `data-step="${escapeHtml(s.name)}" `
          + `data-tip="${escapeHtml(tipAttr([
            s.name,
            `${s.state ? stateName(s.state) : ''}`,
            `Growth ${baseYear}–${latest}: ${signed(s.growth)}`,
            `Share of national growth: ${pct1((s.growth / (netGrowth || 1)) * 100)}`,
            `Share of the ${baseYear} workforce: ${pct1(s.baseShare * 100)}`,
            `Fair-share expectation: ${signed(Math.round(s.fairShare))}`,
            `Shortfall vs expectation: ${signed(Math.round(s.growth - s.fairShare))}`,
          ]))}"><title></title></circle>`,
        );
      }
    });

    const act = steps.find((s) => s.name === 'Australian Capital Territory');
    const actIdx = steps.findIndex((s) => s.name === 'Australian Capital Territory');
    let actCum = 0;
    for (let i = 0; i <= actIdx; i++) actCum += Math.max(0, steps[i].growth);

    root.querySelector('#growth-wrap')!.innerHTML = `
      <svg class="chart" viewBox="0 0 ${W} ${H}" role="img"
           aria-label="Cumulative share of APS growth by region, against a fair-share counterfactual">
        ${[0, 0.25, 0.5, 0.75, 1].map((f) => `
          <line class="gridline" x1="${L}" y1="${yAt(f)}" x2="${W - R}" y2="${yAt(f)}" />
          <text x="${L - 8}" y="${yAt(f) + 4}" font-size="11" text-anchor="end">${(f * 100).toFixed(0)}%</text>`).join('')}
        <path d="${fair.join('')}" fill="none" stroke="#7b8ea1" stroke-width="1.8" stroke-dasharray="6 4" />
        <path d="${actual.join('')}" fill="none" stroke="var(--accent-primary)" stroke-width="2.4" />
        ${marks.join('')}
        <line class="axis" x1="${L}" y1="${T + ih}" x2="${W - R}" y2="${T + ih}" />
        <text x="${L}" y="${H - 14}" font-size="11">Regions ranked by contribution to growth →</text>
        <text x="${W - R}" y="${H - 14}" font-size="11" text-anchor="end">all ${n} regions</text>
        <text x="${L}" y="12" font-size="11" fill="var(--text-tertiary)">Cumulative share of national growth</text>
      </svg>`;

    const svg = root.querySelector<SVGSVGElement>('#growth-wrap svg')!;
    attachSvgZoom(svg);

    const top5 = steps.slice(0, 5).reduce((s, r) => s + r.growth, 0);
    root.querySelector('#growth-sub')!.textContent =
      `The APS grew by ${num(netGrowth)} people between ${baseYear} and ${latest}. `
      + `The five fastest-growing regions account for ${pct1((top5 / (netGrowth || 1)) * 100)} of it. `
      + (act
        ? `The Australian Capital Territory held ${pct1(act.baseShare * 100)} of the workforce in ${baseYear} `
        + `but took ${pct1((act.growth / (netGrowth || 1)) * 100)} of the growth — `
        + `${signed(Math.round(act.growth - act.fairShare))} against what its size predicted.`
        : '');

    // Ledger
    const rows = steps.slice(0, 40);
    root.querySelector('#ledger')!.innerHTML = `
      <thead><tr>
        <th>Region</th><th>State</th><th class="n">${baseYear}</th><th class="n">${latest}</th>
        <th class="n">Change</th><th class="n">%</th><th class="n">Expected</th><th class="n">Vs expected</th>
      </tr></thead>
      <tbody>${rows.map((s) => {
        const r = b.map.regions.find((x) => x.name === s.name)!;
        const then = r.values[String(baseYear)] ?? null;
        const now = r.values[String(latest)] ?? null;
        const diff = s.growth - s.fairShare;
        return `<tr${s.name === pinned ? ' style="outline:2px solid var(--accent-secondary)"' : ''}>
          <td>${escapeHtml(s.name)}</td>
          <td>${escapeHtml(s.state ? stateName(s.state) : '')}</td>
          <td class="n">${num(then)}</td><td class="n">${num(now)}</td>
          <td class="n">${signed(s.growth)}</td>
          <td class="n">${signedPct(growthPct(then, now))}</td>
          <td class="n">${signed(Math.round(s.fairShare))}</td>
          <td class="n" style="color:${diff >= 0 ? 'var(--grow)' : 'var(--shrink)'}">${signed(Math.round(diff))}</td>
        </tr>`;
      }).join('')}</tbody>`;
  }

  sel.addEventListener('change', () => { baseYear = Number(sel.value); draw(); });
  root.addEventListener('click', (e) => {
    const c = (e.target as Element).closest('[data-step]');
    if (!c) return;
    const name = c.getAttribute('data-step')!;
    const row = [...root.querySelectorAll('#ledger tbody tr')]
      .find((tr) => tr.querySelector('td')?.textContent === name);
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    root.querySelectorAll('#ledger tbody tr').forEach((tr) => tr.removeAttribute('style'));
    row?.setAttribute('style', 'outline:2px solid var(--accent-secondary)');
  });

  draw();
}
